/*!
 * o2pPresets.js — Preset System for o2p Touch-Mode Fader Groups
 * Part of oscillaScore control plane
 * © 2025 Rob Canning — GPLv3
 *
 * Features:
 *   - Save/recall fader group positions as named presets
 *   - Tween between presets with easing (interpolates t along path, p for rotation)
 *   - Sequence playback with per-step timing
 *   - localStorage persistence via controlShared.js
 *
 * State per fader: { t, p }
 *   t = normalized path position (0-1)
 *   p = normalized rotation handle position (0-1), absent if no rotation handle
 */

import * as shared from "./controlShared.js";
import { getEasing } from "./easing.js";
import { lerpAngleNorm } from "./rotationMath.js";
import { getVisibilitySnapshot } from "../cues/animShared.js";
import { onReset } from "../system/teardown.js";

// Active tween/sequence state
const activeTweens = new Map(); // groupId -> { rafId, ... }
let activeSequence = null;
let sequenceTimeoutId = null;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get a group from the registry
 */
function getGroup(groupId) {
  return window._o2pTouchGroups?.[groupId] || null;
}

/**
 * List all registered group IDs
 */
export function listGroups() {
  return Object.keys(window._o2pTouchGroups || {});
}

// ============================================================================
// CAPTURE STATE
// ============================================================================

/**
 * Capture current state of a group (or all groups)
 */
// window._o2pIsolatedGroups — a Set of group IDs that should only appear in
// dedicated single-group saves (i.e. when groupFilter === that group's ID).
// Scores or project config can populate this at load time.
// Example:  window._o2pIsolatedGroups = new Set(['polygonControls','synthLevels']);
window._o2pIsolatedGroups = window._o2pIsolatedGroups || new Set();

// ============================================================================
// BUTTON STATE CAPTURE / RESTORE
// Captures toggle:1 and radio button states for groups that registered via
// the group: parameter in their osc() element id.
// ============================================================================

function captureButtonStates(groupFilter = null) {
  const result = {};
  const registry = window._buttonPresetGroups || {};

  for (const [groupId, overlays] of Object.entries(registry)) {
    if (groupFilter && groupId !== groupFilter) continue;

    const groupState = {};
    for (const overlay of overlays) {
      const addr = overlay._oscAddr;
      if (addr) groupState[addr] = overlay._toggleOn ? 1 : 0;
    }
    if (Object.keys(groupState).length > 0) result[groupId] = groupState;
  }
  return result;
}

function applyButtonStates(buttonStates, options = {}) {
  const registry = window._buttonPresetGroups || {};

  for (const [groupId, stateMap] of Object.entries(buttonStates)) {
    const overlays = registry[groupId] || [];

    for (const overlay of overlays) {
      const addr = overlay._oscAddr;
      if (addr === undefined || stateMap[addr] === undefined) continue;

      const wantOn = stateMap[addr] === 1;

      if (overlay._radioGroup) {
        // For radio: fire a click on the one that should be active.
        // Collect which radio group members to update, then update all at once.
        if (wantOn && !overlay._toggleOn) {
          overlay.click();  // fires OSC + sets all radio peers off + this on
        }
      } else {
        // For toggle:1: click to flip if current state doesn't match
        if (overlay._toggleOn !== wantOn) {
          overlay.click();  // fires OSC + toggles visual
        }
      }
    }
  }
}

function captureState(groupFilter = null) {
  const state = {};
  const groups = window._o2pTouchGroups || {};
  const isolated = window._o2pIsolatedGroups;

  for (const [groupId, group] of Object.entries(groups)) {
    if (groupFilter && groupId !== groupFilter) continue;

    // Skip isolated groups when doing a general (multi-group) save.
    // They must be saved explicitly via their own dedicated groupFilter call.
    if (!groupFilter && isolated.has(groupId)) continue;

    if (typeof group.captureState === 'function') {
      const groupState = group.captureState();
      if (groupState && Object.keys(groupState).length > 0) {
        state[groupId] = groupState;
      }
    }
  }

  return state;
}

// ============================================================================
// PRESET MANAGEMENT
// ============================================================================

/**
 * Save current fader positions as a named preset
 */
export function savePreset(name, groupFilter = null) {
  if (!name) {
    console.warn("[o2p] savePreset: name required");
    return false;
  }

  const positions = captureState(groupFilter);

  if (Object.keys(positions).length === 0) {
    console.warn("[o2p] savePreset: no o2p groups found or no faders registered");
    return false;
  }

  // Also capture visibility state for UI toggles
  const visibility = getVisibilitySnapshot() || {};

  // Capture button states (toggle:1 and radio buttons tagged with group:)
  const buttons = captureButtonStates(groupFilter);

  const data = {
    ...positions,
    _meta: { savedAt: shared.nowMs(), filter: groupFilter },
    _visibility: visibility,
    _buttons: buttons
  };

  shared.saveByKind("o2pPreset", name, data);

  window.dispatchEvent(new CustomEvent('o2p:presetSaved', {
    detail: { name }
  }));

  return true;
}

/**
 * Save preset from explicit data (for programmatic use)
 */
export function savePresetFromData(name, data, options = {}) {
  if (!name || !data) {
    console.warn("[o2p] savePresetFromData: name and data required");
    return false;
  }

  // Normalize: clamp t and p to 0-1
  const normalizedState = {};

  for (const [groupId, faders] of Object.entries(data)) {
    if (groupId === '_meta' || typeof faders !== 'object') continue;

    const normalizedFaders = {};
    for (const [faderId, pos] of Object.entries(faders)) {
      if (typeof pos !== 'object') continue;

      const faderData = {};

      if (typeof pos.t === 'number') {
        faderData.t = Math.round(Math.max(0, Math.min(1, pos.t)) * 1000) / 1000;
      }

      if (typeof pos.p === 'number') {
        faderData.p = Math.round(Math.max(0, Math.min(1, pos.p)) * 1000) / 1000;
      }

      if (Object.keys(faderData).length > 0) {
        normalizedFaders[faderId] = faderData;
      }
    }

    if (Object.keys(normalizedFaders).length > 0) {
      normalizedState[groupId] = normalizedFaders;
    }
  }

  if (Object.keys(normalizedState).length === 0) {
    console.warn("[o2p] savePresetFromData: no valid position data found");
    return false;
  }

  const presetData = {
    ...normalizedState,
    _meta: { savedAt: shared.nowMs(), source: 'data', ...options }
  };

  shared.saveByKind("o2pPreset", name, presetData);
  return true;
}

/**
 * Delete a preset
 */
export function deletePreset(name) {
  const result = shared.deleteByKind("o2pPreset", name);
  if (result) {
    console.log(`[o2p] Deleted preset "${name}"`);
    window.dispatchEvent(new CustomEvent('o2p:presetDeleted', { detail: { name } }));
  }
  return result;
}

/**
 * List all o2p preset names
 */
export function listPresets() {
  return shared.listByKind("o2pPreset");
}

/**
 * Get preset data by name
 */
export function getPreset(name) {
  const item = shared.getByKind("o2pPreset", name);
  return item?.data || null;
}

/**
 * Recall a preset by name
 * @param {string} name - Preset name
 * @param {Object} options - Options
 * @param {number} options.dur - Tween duration in seconds
 * @param {string} options.ease - Easing function
 * @param {boolean} options._remote - True if this is a remote recall (don't re-broadcast)
 */
export function recallPreset(name, options = {}) {
  const presetData = getPreset(name);

  if (!presetData) {
    console.warn(`[o2p] Preset "${name}" not found`);
    return false;
  }

  // Persist last recalled preset to localStorage so it's restored on next load
  if (!options.silent && !options._remote) {
    try {
      const key = `o2p_lastRecalled_${shared.getProjectName()}`;
      localStorage.setItem(key, name);
    } catch { /* storage blocked (private mode) */ }
  }

  const { dur = 0, ease = 'easeInOutSine', _remote = false } = options;

  // Extract position data (exclude meta/visibility/buttons keys)
  const positions = {};
  for (const [key, val] of Object.entries(presetData)) {
    if (key !== '_meta' && key !== '_visibility' && key !== '_buttons') {
      positions[key] = val;
    }
  }

  if (dur <= 0) {
    applyPositions(positions);
  } else {
    tweenTo(positions, { dur, ease });
  }

  // Apply visibility state if present. Called through window, which
  // extensions wrap: polygonfield keeps the polygons a performer's role
  // shows from being hidden by a recall.
  if (presetData._visibility) {
    window._applyVisibilitySnapshot(presetData._visibility, dur > 0 ? dur : 0.3);
  }

  // Restore button states (toggle:1 and radio buttons)
  if (presetData._buttons && Object.keys(presetData._buttons).length > 0) {
    // Delay slightly so fader animations have started before buttons fire OSC
    setTimeout(() => applyButtonStates(presetData._buttons), 50);
  }

  window.dispatchEvent(new CustomEvent('o2p:presetRecalled', {
    detail: { name, dur }
  }));

  // Broadcast to other clients when in composition scope
  // Send resolved positions so they don't need the preset in their storage
  if (!_remote) {
    const scope = shared.getPresetScope() || 'composition';
    if (scope === 'composition') {
      const sock = window.socket;
      if (sock && sock.readyState === WebSocket.OPEN) {
        sock.send(JSON.stringify({
          type: "o2p_preset_sync",
          action: "recall",
          name,
          dur,
          ease,
          positions
        }));
      }
    }
  }

  return true;
}

// ============================================================================
// APPLY POSITIONS
// ============================================================================

/**
 * Apply positions instantly to groups
 * positions: { groupId: { faderId: { t, p }, ... }, ... }
 */
function applyPositions(positions) {
  for (const [groupId, faderPositions] of Object.entries(positions)) {
    const group = getGroup(groupId);
    if (!group) {
      console.warn(`[o2p] applyPositions: group "${groupId}" not found`);
      continue;
    }

    if (typeof group.applyPositions === 'function') {
      group.applyPositions(faderPositions);
    }
  }
}

// ============================================================================
// TWEENING
// ============================================================================

/**
 * Tween groups to target positions
 * positions: { groupId: { faderId: { t, p }, ... }, ... }
 */
export function tweenTo(positions, options = {}) {
  const { dur = 1, ease = 'easeInOutSine', onComplete } = options;
  const easeFunc = getEasing(ease);
  const durationMs = dur * 1000;

  // Stop any active tweens for affected groups
  for (const groupId of Object.keys(positions)) {
    if (activeTweens.has(groupId)) {
      cancelAnimationFrame(activeTweens.get(groupId).rafId);
      activeTweens.delete(groupId);
    }
  }

  // Capture start positions for exactly the groups being tweened. A bare
  // captureState() skips isolated groups (window._o2pIsolatedGroups), which
  // left them without a start and tweened every fader up from t=0 — a dip to
  // silence on level faders, a full-speed reverse lurch on bipolar speeds.
  const startPositions = {};
  for (const groupId of Object.keys(positions)) {
    Object.assign(startPositions, captureState(groupId));
  }
  const startTime = performance.now();

  function animate() {
    const elapsed = performance.now() - startTime;
    const progress = Math.min(1, elapsed / durationMs);
    const easedProgress = easeFunc(progress);

    for (const [groupId, faderPositions] of Object.entries(positions)) {
      const group = getGroup(groupId);
      if (!group) continue;

      const startGroup = startPositions[groupId] || {};

      for (const [faderId, targetPos] of Object.entries(faderPositions)) {
        if (!targetPos || typeof targetPos.t !== 'number') continue;

        const startPos = startGroup[faderId];
        const startT = startPos?.t ?? 0;

        // Interpolate t (simple linear lerp along path)
        const currentT = startT + (targetPos.t - startT) * easedProgress;

        // Interpolate p if present
        let currentP = undefined;
        if (typeof targetPos.p === 'number') {
          const startP = startPos?.p ?? 0;
          const fader = group.faders?.[faderId];

          if (fader?.hmode === 'continuous') {
            // Shortest-arc interpolation for continuous rotation
            currentP = lerpAngleNorm(startP, targetPos.p, easedProgress);
          } else {
            // Simple lerp for limited mode
            currentP = startP + (targetPos.p - startP) * easedProgress;
          }
        }

        group.setPosition(faderId, currentT, currentP);
      }
    }

    if (progress < 1) {
      const rafId = requestAnimationFrame(animate);
      for (const groupId of Object.keys(positions)) {
        activeTweens.set(groupId, { rafId, startTime, durationMs });
      }
    } else {
      for (const groupId of Object.keys(positions)) {
        activeTweens.delete(groupId);
      }
      onComplete?.();
    }
  }

  animate();
}

/**
 * Stop all active tweens
 */
export function stopAllTweens() {
  for (const [, state] of activeTweens) {
    cancelAnimationFrame(state.rafId);
  }
  activeTweens.clear();
}

// ============================================================================
// SEQUENCES
// ============================================================================

/**
 * Define a named sequence
 */
export function defineSequence(name, steps, options = {}) {
  if (!name || !Array.isArray(steps)) {
    console.warn("[o2p] defineSequence: name and steps array required");
    return false;
  }

  shared.saveByKind("o2pSequence", name, {
    steps,
    loop: options.loop ?? false,
    defaultDur: options.dur,
    defaultEase: options.ease
  });

  return true;
}

/**
 * Get sequence data
 */
export function getSequence(name) {
  const item = shared.getByKind("o2pSequence", name);
  return item?.data || null;
}

/**
 * List all o2p sequence names
 */
export function listSequences() {
  return shared.listByKind("o2pSequence");
}

/**
 * Play a sequence
 */
export function playSequence(name, options = {}) {
  const seqData = getSequence(name);

  if (!seqData) {
    console.warn(`[o2p] Sequence "${name}" not found`);
    return false;
  }

  stopSequence();

  const steps = seqData.steps || seqData;
  const loop = options.loop ?? seqData.loop ?? false;
  const defaultDur = options.dur ?? seqData.defaultDur ?? 1;
  const defaultEase = options.ease ?? seqData.defaultEase ?? 'easeInOutSine';

  let currentStep = 0;
  let loopCount = 0;
  const maxLoops = typeof loop === 'number' ? loop : (loop ? Infinity : 1);

  activeSequence = {
    name,
    steps,
    currentStep,
    loop: maxLoops,
    loopCount,
    stopped: false,
    running: true
  };

  function playStep() {
    if (!activeSequence || activeSequence.stopped) {
      activeSequence = null;
      sequenceTimeoutId = null;
      return;
    }

    if (currentStep >= steps.length) {
      loopCount++;
      if (loopCount >= maxLoops) {
        activeSequence = null;
        sequenceTimeoutId = null;
        window.dispatchEvent(new CustomEvent('o2p:sequenceComplete', {
          detail: { name }
        }));
        return;
      }
      currentStep = 0;
    }

    const step = steps[currentStep];
    const presetName = typeof step === 'string' ? step : step.preset;
    const stepDur = typeof step === 'object' ? (step.dur ?? defaultDur) : defaultDur;
    const stepEase = typeof step === 'object' ? (step.ease ?? defaultEase) : defaultEase;

    recallPreset(presetName, {
      dur: stepDur,
      ease: stepEase
    });

    window.dispatchEvent(new CustomEvent('o2p:sequenceStep', {
      detail: {
        name,
        preset: presetName,
        step: currentStep + 1,
        total: steps.length,
        loopCount
      }
    }));

    currentStep++;
    if (activeSequence) activeSequence.currentStep = currentStep;

    sequenceTimeoutId = setTimeout(playStep, stepDur * 1000);
  }

  playStep();

  console.log(`[o2p] Playing sequence "${name}" (${steps.length} steps, loop: ${maxLoops})`);

  window.dispatchEvent(new CustomEvent('o2p:sequenceStarted', {
    detail: { name, steps: steps.length, loop: maxLoops }
  }));

  return true;
}

/**
 * Stop current sequence
 */
// a sequence or a tween still stepping would recall the next project's presets
onReset("project", function stopPresetSequence() { stopSequence(); });

export function stopSequence() {
  const wasPlaying = activeSequence?.name;

  if (sequenceTimeoutId) {
    clearTimeout(sequenceTimeoutId);
    sequenceTimeoutId = null;
  }

  if (activeSequence) {
    activeSequence.stopped = true;
    console.log(`[o2p] Stopped sequence "${activeSequence.name}"`);
  }
  activeSequence = null;
  stopAllTweens();

  window.dispatchEvent(new CustomEvent('o2p:sequenceStopped', {
    detail: { name: wasPlaying }
  }));
}

/**
 * Get active sequence info
 */
export function getActiveSequence() {
  return activeSequence;
}

// ============================================================================
// PERSISTENCE INIT
// ============================================================================

let autoRecallTimer = null;   // the pending auto-recall, so a second init does not schedule another

function initPresets(projectName) {
  if (!shared.state.initialized) {
    shared.init(projectName);
  }

  // Auto-recall on load: the preset this client last recalled (localStorage).
  // Once per load (autoInit and the project loader both call this), and for
  // this client only: _remote keeps it off the room, which would otherwise
  // move every client's faders to one tablet's last preset.
  const lastRecalledKey = `o2p_lastRecalled_${projectName}`;
  const toRecall = (() => { try { return localStorage.getItem(lastRecalledKey); } catch(e) { return null; } })();
  clearTimeout(autoRecallTimer);
  autoRecallTimer = null;
  if (toRecall) {
    console.log(`[o2pPresets] auto-restoring "${toRecall}" on load (last used)`);
    autoRecallTimer = setTimeout(() => {
      autoRecallTimer = null;
      recallPreset(toRecall, { dur: 1.5, silent: true, _remote: true });
    }, 800);
  }
}

export function init(projectName) {
  initPresets(projectName || shared.getProjectName());
}

export function forceSave() {
  shared.forceSave();
}

// ============================================================================
// UTILITY PRESETS
// Auto-generate "off" and "on" presets for a group
// ============================================================================

/**
 * Create utility presets for a group:
 *   - "off" — all faders at t=0 (mute/minimum)
 *   - "on"  — all faders at t=0.8 (unity/0dB with standard dB scaling)
 *
 * @param {string} groupId - The group to create presets for
 * @param {Object} [opts] - Options
 * @param {number} [opts.offT=0] - t value for "off" preset
 * @param {number} [opts.onT=0.8] - t value for "on" preset
 * @param {boolean} [opts.assignToSlots=true] - Auto-assign to launcher slots 1 and 2
 */
export function createUtilityPresets(groupId, opts = {}) {
  const group = getGroup(groupId);
  if (!group) {
    console.warn(`[o2p] createUtilityPresets: group "${groupId}" not found`);
    return false;
  }

  const offT = opts.offT ?? 0;
  const onT = opts.onT ?? 0.8;
  const assignToSlots = opts.assignToSlots !== false;

  const faderIds = Object.keys(group.faders || {});
  if (faderIds.length === 0) {
    console.warn(`[o2p] createUtilityPresets: no faders in group "${groupId}"`);
    return false;
  }

  // Build preset data
  const offData = { [groupId]: {} };
  const onData = { [groupId]: {} };

  for (const faderId of faderIds) {
    offData[groupId][faderId] = { t: offT };
    onData[groupId][faderId] = { t: onT };
  }

  // Save presets with group-specific names to avoid conflicts
  const offName = `${groupId}_off`;
  const onName = `${groupId}_on`;

  savePresetFromData(offName, offData, { silent: true });
  savePresetFromData(onName, onData, { silent: true });

  // Auto-assign to launcher slots 1 and 2
  if (assignToSlots) {
    assignPresetToSlot(offName, groupId, 0);  // Slot 1
    assignPresetToSlot(onName, groupId, 1);   // Slot 2
  }

  console.log(`[o2p] Created utility presets "${offName}" (t=${offT}) and "${onName}" (t=${onT}) for group "${groupId}"`);
  return true;
}

/**
 * Assign a preset to a launcher slot
 * @param {string} presetName - Name of the preset to assign
 * @param {string} launcherUid - The launcher UID (typically the group ID)
 * @param {number} slotIdx - Slot index (0-5 for slots 1-6)
 * @param {number} [bankIdx=0] - Bank index (default 0)
 */
export function assignPresetToSlot(presetName, launcherUid, slotIdx, bankIdx = 0) {
  // Get or create launcher data
  let launcherData = shared.getOrCreateLauncherByKind('o2pLauncher', launcherUid, {
    currentBank: 0,
    mode: 'preset',
    tween: true,
    visible: true,
    banks: []
  });

  if (!launcherData) {
    launcherData = { banks: [] };
  }

  // Ensure bank exists
  while (launcherData.banks.length <= bankIdx) {
    launcherData.banks.push({
      name: 'Bank ' + (launcherData.banks.length + 1),
      slots: Array(6).fill(null)
    });
  }

  // Ensure slots array exists
  if (!launcherData.banks[bankIdx].slots) {
    launcherData.banks[bankIdx].slots = Array(6).fill(null);
  }

  // Assign the preset
  launcherData.banks[bankIdx].slots[slotIdx] = {
    type: 'preset',
    name: presetName
  };

  // Save
  shared.saveLauncherByKind('o2pLauncher', launcherUid, launcherData);
  shared.save();

  // Refresh launcher UI
  window.dispatchEvent(new CustomEvent('o2p:launcherRefresh', { detail: { uid: launcherUid } }));
}

// ============================================================================
// GLOBAL API
// ============================================================================

window.o2pPresets = {
  // Groups
  listGroups,

  // Presets
  save: savePreset,
  saveFromData: savePresetFromData,
  recall: recallPreset,
  delete: deletePreset,
  list: listPresets,
  get: getPreset,

  // Tweening
  tweenTo,
  stopAllTweens,

  // Sequences
  defineSequence,
  getSequence,
  listSequences,
  playSequence,
  stopSequence,
  getActiveSequence,

  // Persistence
  init,
  forceSave,

  // Utility presets
  createUtilityPresets,
  assignPresetToSlot,

  // Debug
  _shared: shared,
  _activeTweens: activeTweens,
  _captureState: captureState,
};

// Auto-initialize
function autoInit() {
  const projectName = shared.getProjectName();
  if (projectName && projectName !== 'unknown_project') {
    initPresets(projectName);
  } else {
    const checkInterval = setInterval(() => {
      const name = shared.getProjectName();
      if (name && name !== 'unknown_project') {
        clearInterval(checkInterval);
        initPresets(name);
      }
    }, 200);
    setTimeout(() => clearInterval(checkInterval), 10000);
  }
}

autoInit();

// ============================================================================
// REMOTE SYNC — apply preset/sequence commands from other clients
// ============================================================================

window.addEventListener("oscilla:o2p-preset-sync", (e) => {
  const { action, name, dur, ease, positions } = e.detail || {};

  if (action === "recall") {

    // Apply positions directly (we received resolved positions, not preset name)
    if (positions && Object.keys(positions).length > 0) {
      if (dur <= 0) {
        applyPositions(positions);
      } else {
        tweenTo(positions, { dur, ease: ease || 'easeInOutSine' });
      }
    } else {
      // Fallback: try to recall by name if we have the preset locally
      recallPreset(name, { dur, ease, _remote: true });
    }
  }
});

